import { Feature as SharedFeature } from "@pedagogue/entitlements-types";
import type { PlanTier } from "@pedagogue/entitlements-types";

export type MediaType =
  | "image"
  | "video"
  | "text"
  | "rive"
  | "multipleChoice"
  | "html"
  | "lottie";

export const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export interface userData {
  id: string;
  name: string;
  email: string;
  xp: number;
  current_streak: number;
  total_courses_assigned?: number;
  total_courses_completed?: number;
  sector?: string;
  role_in_org?: string;
  job_title?: string | null;
  full_name?: string;
  avatar_url?: string | null;
  role?: string;
  trial_expires_at?: string | null;
  first_login_at?: string | null;
  last_login_at?: string | null;
  // Org-level trial context (from /users/me). A trial-org ADMIN has no
  // profile-level trial_expires_at, so the in-trial conversion CTA reads these.
  organisation_id?: string | null;
  is_trial_org?: boolean;
  trial_kind?: "full" | "sample" | null;
  subscription_valid_until?: string | null;
  organisation_logo_url?: string | null;
  // SOCS buyer persona (WS-O). Only a decision-maker sees the in-trial buy CTA;
  // a non-buyer is steered to "invite your decision-maker". NULL for non-SOCS.
  buyer_type?: string | null;
}

export interface RecentCourse {
  id: string;
  title: string;
  description: string;
  progress_percentage: number;
  icon: string;
  course_type: string;
  lastAccessedAt: string;
  category: string;
  estimated_total_duration_minutes: number;
  level_label: string;
}

export interface Suggestion {
  id: string;
  suggested_course_title: string;
  reason_for_suggestion: string;
  icon: string;
  category: string;
  estimated_total_duration_minutes: number;
  course_type: string;
  level_label: string;
}

export interface PedaConversationRequest {
  session_id: string;
  course_id: string;
  lesson_id: string;
  card_id: string;
  user_message: string;
}

export interface PedaConversation {
  peda_response: string;
}

export interface PedaEvaluationRequest {
  session_id: string;
  lesson_id: string;
  card_id: string;
  learner_response: string;
}

export interface PedaEvaluationResponse {
  outcome_code: "E" | "M" | "U";
  outcome_label: "Exceeds" | "Meets" | "Under";
  outcome_message: string;
  feedback: string;
  feedforward: string;
  word_count: number;
  conversation_id?: string;
}

export interface ModuleLesson {
  id: string;
  title: string;
  status: "completed" | "unlocked" | "locked";
}

export interface CourseModule {
  id: string;
  title: string;
  description: string;
  lessons: ModuleLesson[];
}

export interface CourseWithModules {
  id: string;
  title: string;
  description: string;
  estimated_total_duration_minutes: number;
  reward_xp: number;
  is_completed: boolean;
  progress_percentage: number;
  modules: CourseModule[];
}

export type LessonTimeLine = {
  id: string;
  title: string;
  status: "locked" | "unlocked" | "completed";
  isOptional?: boolean;
};

export interface Remediation {
  type: "text" | "video" | "link";
  content: string;
}

// 1 : Card Type : Learning Outcomes :
export interface LearningOutcomesContent {
  slide_title: string;
  intro_text: string;
  title: string;
  outcomes: string[];
  image_uri?: string;
  alt_text?: string;
}

// 2 : Shared option shape used by older card payloads :
export interface QuestionOption {
  text: string;
  is_correct: boolean;
  remediation: Remediation | null;
}

// 3 : Card Type : Information :
export interface InformationContent {
  slide_title: string;
  text: string;
  image_uri?: string;
  alt_text?: string;
}

// 4 : Card Type : Flashcard :
export interface FlashcardItem {
  label: string;
  front_text: string;
  front_image_uri: string | null;
  front_alt_text?: string;
  back_text: string;
  back_image_uri: string | null;
  back_alt_text?: string;
}

export interface FlashcardContent {
  slide_title: string;
  intro_text: string;
  items: FlashcardItem[];
}

// 5 : Card Type : Reveal Text Strip :
export interface RevealTextItem {
  prompt: string;
  label?: string;
  icon_uri?: string;
  image_uri?: string;
  revealed_text: string;
}

export interface RevealTextStripContent {
  slide_title: string;
  items: RevealTextItem[];
}

export interface ImageHotspot {
  x_percentage: number;
  y_percentage: number;
  reveal_title: string;
  reveal_text: string;
}

export interface ImageHotspotContent {
  slide_title: string;
  intro_text: string;
  image_uri: string;
  alt_text: string;
  hotspots: ImageHotspot[];
}

export interface SequenceSortItem {
  id: string;
  text: string;
}

export interface SequenceSortContent {
  slide_title: string;
  intro_text: string;
  prompt: string;
  items: SequenceSortItem[];
  correct_order: string[];
  feedback: {
    correct_title: string;
    correct_text: string;
    incorrect_title: string;
    incorrect_text: string;
  };
  max_score: number;
  awarded_tags: string[];
}

// 4 : Card Type : Video :
export interface VideoContent {
  slide_title: string;
  intro_text: string;
  video_url?: string;
  video_uri?: string;
  transcript: string;
}

// 5 : Card Type : Quick Recap :
export interface QuickRecapContent {
  slide_title: string;
  intro_text: string;
  title?: string;
  recap_label?: string;
  points: string[];
  additional_text?: string;
  show_certificate_link?: boolean;
}

// 6 : Card Type : Match Exercise :
export interface MatchExerciseContent {
  slide_title: string;
  intro_text: string;
  prompt?: string;
  image_uri?: string;
  // Legacy format: pairs array
  pairs?: {
    item: string;
    match: string;
    correct_feedback?: string;
  }[];
  // New format: items + targets + correct_matches
  items?: {
    id: string;
    text: string;
    correct_feedback?: string;
  }[];
  targets?: {
    id: string;
    label: string;
  }[];
  correct_matches?: Record<string, string>;
  feedback: {
    correct_title: string;
    correct_text: string;
    incorrect_title: string;
    incorrect_text: string;
  };
  max_score?: number;
  awarded_tags?: string[];
}

// 7 : Card Type : Data Table :
export interface DataTableContent {
  slide_title: string;
  intro_text: string;
  title: string;
  headers: string[];
  // Support both array of arrays and array of objects formats
  // Backend should transform objects to arrays, but frontend handles both for compatibility
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: string[][] | Record<string, any>[];
}

// 8 : Card Type : Data Visualization :
export interface DataVisualizationContent {
  slide_title: string;
  intro_text: string;
  note?: string;
  chart_type: "bar" | "bar chart" | "pie" | "table";
  data?: {
    labels: string[];
    datasets: Array<{
      label: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: number[] | any[];
      backgroundColor?: string[];
    }>;
  };
  column_headers?: string[];
  table_data?: Array<Record<string, string | number>>;
  question: string;
  options: Array<{
    text: string;
    is_correct: boolean;
    remediation?: {
      type: string;
      content: string;
    };
    feedback_text?: string;
  }>;
  max_score?: number;
  awarded_tags?: string[];
}

// 9 : Card Type : Knowledge Check (Interactive MCQ) :
export interface KnowledgeCheckContent {
  slide_title: string;
  intro_text: string;
  question: string;
  options: Array<{
    text: string;
    is_correct: boolean;
    remediation?: {
      type: string;
      content: string;
    };
  }>;
  max_score: number;
  awarded_tags: string[];
}

// 10 : Card Type : Quick Question (Non-interactive Feedback) :
export interface QuickQuestionContent {
  slide_title: string;
  intro_text: string;
  question_text?: string;
  question?: string;
  answer_options?: string[];
  options?: Array<{
    text: string;
    tag_value?: string;
    is_correct?: boolean;
    remediation?: Remediation | null;
  }>;
  tag_key?: string;
}

// 11 : Card Type : Drag and Drop Sorter :
export interface DragAndDropSorterContent {
  slide_title: string;
  intro_text: string;
  prompt: string;
  items: Array<{
    id: string;
    text: string;
  }>;
  categories: Array<{
    title: string;
    correct_item_ids: string[];
  }>;
  feedback: {
    correct_title: string;
    correct_text: string;
    incorrect_title: string;
    incorrect_text: string;
  };
  max_score: number;
  awarded_tags: string[];
}

export interface RetiredAssessmentContent {
  [key: string]: unknown;
}

export interface PedaPreviewContent {
  slide_title?: string;
  intro_text?: string;
  prompt_text?: string;
}

export interface PollContent {
  slide_title?: string;
  intro_text?: string;
  image_uri?: string;
  alt_text?: string;
  question: string;
  options: Array<{
    text: string;
    tag_value: string;
  }>;
  tag_key?: string;
}

export interface LessonCard {
  id: string;
  type:
    | "quick_question"
    | "knowledge_check"
    | "match_exercise"
    | "drag_and_drop_sorter"
    | "image_hotspot"
    | "data_visualization"
    | "sequence_sort"
    | "information"
    | "video"
    | "data_table"
    | "flashcard"
    | "quick_recap"
    | "learning_outcomes"
    | "reveal_text_strip"
    | "peda"
    | "poll"
    | "assessment";
  content:
    | QuickQuestionContent
    | KnowledgeCheckContent
    | MatchExerciseContent
    | DragAndDropSorterContent
    | ImageHotspotContent
    | DataVisualizationContent
    | SequenceSortContent
    | InformationContent
    | VideoContent
    | DataTableContent
    | FlashcardContent
    | QuickRecapContent
    | LearningOutcomesContent
    | RevealTextStripContent
    | RetiredAssessmentContent
    | PedaPreviewContent
    | PollContent;
  base_xp?: number;
}

export interface Lesson {
  id: string;
  course_id?: string;
  content_version?: number;
  title: string;
  description: string;
  completion_time?: number; // legacy — not returned by /lessons/:id
  estimated_duration_minutes?: number; // actual API field
  icon: string | null;
  cards: LessonCard[];
  is_optional?: boolean;
  learner_progress?: {
    is_completed: boolean;
    skipped_at: string | null;
  } | null;
}

export type Certificate = {
  id: string;
  user_name: string;
  course_name: string;
  course_description: string;
  issued_at: string;
  download_uri: string | null;
  pdf_status?: "ready" | "generating" | "failed";
  thumbnail_uri?: string | null;
  organisation_name?: string;
  // Immutable Pedagogue-certification status captured when this was issued.
  isPedagogueCertified?: boolean;
  // RETIRED DISPLAY STATE — always false as of 2026-08-29 (CTO product ruling).
  // The SAMPLE watermark this fed is removed entirely; a certificate earned
  // inside the 7-day trial is a real certificate. The historical description of
  // the field follows, kept because it explains why the field exists at all.
  //
  // (historical) IMMUTABLE PROVENANCE: true when the certificate was EARNED during trial.
  // Resolved by the backend's `wasTrialAtIssue` (a replay of trial_events
  // against issued_at), NOT from organisations.is_trial_org at fetch time —
  // that earlier behaviour is retired.
  //
  // ⚠ The watermark this flag fed was RETIRED 2026-08-29 (CTO product ruling);
  // nothing renders it now. The wire FIELD is unchanged and still sent; its VALUE is now
  // always false (see openapi and api-reference).
  // History, kept because a reinstatement would get it wrong: this flag alone
  // never decided the watermark. G12 introduced trial->paid re-certification, so a
  // certificate can be trial-at-issue AND certified and must then render clean.
  // The display predicate was the conjunction
  // `isTrial && !isPedagogueCertified` — see
  // backend/.../certificates/certificate-render-state.ts for why neither flag
  // works alone (isPedagogueCertified is also false for a non-certified course
  // and for a feature-denied plan, so watermarking on it would stamp
  // "SAMPLE - TRIAL" on a paying customer's certificate).
  isTrial?: boolean;
  // Optional fields for backward compatibility with certificates list
  score?: number;
  course_id?: string;
  lesson_id?: string;
};

// Lesson completion types
export interface CardScore {
  card_id: string;
  score: number;
  mistakes: number;
}

export interface LessonCompleteCardScoreResponse extends CardScore {
  base_xp: number;
  earned_xp: number;
  xp_percentage: number;
}

export interface LessonCompleteRequest {
  card_scores: CardScore[];
  // v11: pass client local date/timezone for streaks
  local_date?: string; // YYYY-MM-DD
  local_offset_minutes?: number; // minutes offset from UTC
  time_zone?: string; // IANA timezone name
}

export interface LessonCompleteResponse {
  id: string;
  user_id: string;
  lesson_id: string;
  organisation_id: string | null;
  is_completed: boolean;
  completed_at: string | null;
  soft_lock_message?: string | null;
  total_score: number;
  scores_by_card: LessonCompleteCardScoreResponse[];
  total_earned_xp: number;
  lesson_xp_percentage: number;
}

// ============================================================================
// v11: Master Course types
// These types support the master course hierarchy introduced in Item 8
// ============================================================================

/**
 * Basic level (course) information within a master course.
 */
export interface CourseLevel {
  id: string;
  title: string;
  levelNumber: number;
  estimatedDurationMinutes: number;
  rewardXp: number;
}

/**
 * Level with progress information for authenticated users.
 */
export interface CourseLevelWithProgress extends CourseLevel {
  progressPercentage: number;
  isCompleted: boolean;
  status: "completed" | "in_progress" | "not_started";
}

/**
 * Basic master course information.
 * A master course is the parent "Course" that groups one or more levels.
 */
export interface MasterCourse {
  id: string;
  title: string;
  description: string | null;
  iconUri: string | null;
  certificatePolicy: string;
  totalLevels: number;
  estimatedTotalDurationMinutes: number;
  totalRewardXp: number;
  levels: CourseLevel[];
}

/**
 * Master course with progress information for authenticated users.
 * Includes computed fields like overall progress percentage and completion status.
 */
export interface MasterCourseWithProgress extends MasterCourse {
  progressPercentage: number;
  completedLevels: number;
  isCompleted: boolean;
  levels: CourseLevelWithProgress[];
}

/**
 * v11 Certificate type with scope information.
 * Extends the base Certificate type with master course fields.
 */
export interface CertificateV11 extends Certificate {
  scope?: "level" | "course";
  master_course_id?: string;
  master_course_title?: string;
}

// ============================================================================
// Entitlements types
// ============================================================================

export type { PlanTier };

export const Feature = SharedFeature;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Feature = `${SharedFeature}`;

/**
 * Entitlements data returned from the API
 */
export interface EntitlementsData {
  planTier: PlanTier;
  subscriptionValidUntil: string | null;
  features: Feature[];
  featureOverrides: Record<string, boolean>;
  isSubscriptionExpired: boolean;
  isTrialOrg: boolean;
  trialKind: "full" | "sample" | null;
  packageSku: string | null;
}
